import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Star, CheckCircle2, Loader2 } from "lucide-react";
import { compressImageFile, makeThumbnail } from "@/lib/imageOptimize";

export default function CreateAuctionPage() {
  const { user, authHeaders } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [images, setImages] = useState([]); // [{id, data, thumb}]
  const [main, setMain] = useState(0);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [payment, setPayment] = useState(null);
  const [f, setF] = useState({
    brand: "", model: "", year: "", body_type: "", engine_size: "", fuel_type: "", transmission: "",
    drive_type: "", mileage: "", color: "", city: "", condition: "", description: "",
    starting_price: "", min_bid_increment: "100", start_time: "", end_time: "",
  });

  if (!user) { nav("/login"); return null; }

  const upd = (k, v) => setF({ ...f, [k]: v });

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - images.length);
    if (!files.length) return;
    setUploadingCount(files.length);
    try {
      for (const file of files) {
        const data = await compressImageFile(file);
        const thumb = await makeThumbnail(data);
        setImages(prev => [...prev, {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          data, thumb,
        }]);
      }
    } finally {
      setUploadingCount(0);
      e.target.value = ""; // allow re-selecting same file
    }
  };

  const submit = async () => {
    if (images.length === 0) { toast.error("Ən azı 1 şəkil yükləyin"); return; }
    try {
      const payload = {
        vehicle: {
          brand: f.brand, model: f.model, year: parseInt(f.year),
          body_type: f.body_type, engine_size: parseFloat(f.engine_size),
          fuel_type: f.fuel_type, transmission: f.transmission, drive_type: f.drive_type,
          mileage: parseInt(f.mileage), color: f.color, city: f.city,
          condition: f.condition, description: f.description,
        },
        starting_price: parseFloat(f.starting_price),
        min_bid_increment: parseFloat(f.min_bid_increment),
        start_time: new Date(f.start_time).toISOString(),
        end_time: new Date(f.end_time).toISOString(),
        images: images.map(x => x.data), main_image_index: main,
        thumbnail: images[main]?.thumb,
      };
      const { data } = await axios.post(`${API}/auctions`, payload, { headers: authHeaders() });
      setPayment(data.payment);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Xəta"); }
  };

  const startPayriff = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/${payment.id}/payriff/checkout`, {}, { headers: authHeaders() });
      if (data.already_paid) { toast.success("Artıq ödənilib"); nav("/dashboard"); return; }
      if (data.payment_url) { window.location.href = data.payment_url; }
    } catch (e) { toast.error(e.response?.data?.detail || "Ödəniş başladıla bilmədi"); }
  };

  const mockPay = async () => {
    try {
      await axios.post(`${API}/payments/${payment.id}/mock-pay`, {}, { headers: authHeaders() });
      toast.success("Ödəniş uğurla tamamlandı (test)");
      nav("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Xəta"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 fade-in">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Avtomobilimi sat</h1>
        <p className="text-slate-500 mt-1">Auksion yaratmaq üçün formu doldurun (10 AZN listinq haqqı)</p>
      </div>

      <div className="flex items-center gap-2 mb-8">
        {[1,2,3].map(n => (
          <div key={n} className={`flex-1 h-2 rounded-full transition-colors ${step >= n ? "bg-blue-600" : "bg-slate-200"}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-bold text-xl mb-2">Avtomobil məlumatları</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Marka *"><Input data-testid="veh-brand" value={f.brand} onChange={e => upd("brand", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Model *"><Input data-testid="veh-model" value={f.model} onChange={e => upd("model", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Buraxılış ili *"><Input data-testid="veh-year" type="number" value={f.year} onChange={e => upd("year", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Kuzov tipi *">
              <Select value={f.body_type} onValueChange={v => upd("body_type", v)}>
                <SelectTrigger data-testid="veh-body" className="rounded-xl h-11"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{["Sedan","Hetçbek","Universal","Krossover","SUV","Kupe","Kabriolet","Pikap"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Mühərrik həcmi (L) *"><Input data-testid="veh-engine" type="number" step="0.1" value={f.engine_size} onChange={e => upd("engine_size", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Yanacaq *">
              <Select value={f.fuel_type} onValueChange={v => upd("fuel_type", v)}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{["Benzin","Dizel","Qaz","Hibrid","Elektrik"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Ötürücü qutusu *">
              <Select value={f.transmission} onValueChange={v => upd("transmission", v)}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{["Mexanika","Avtomat","Robot","Variator"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Ötürücü *">
              <Select value={f.drive_type} onValueChange={v => upd("drive_type", v)}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{["Ön","Arxa","Tam"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Yürüş (km) *"><Input data-testid="veh-mileage" type="number" value={f.mileage} onChange={e => upd("mileage", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Rəng *"><Input value={f.color} onChange={e => upd("color", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Şəhər *"><Input data-testid="veh-city" value={f.city} onChange={e => upd("city", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Vəziyyət *">
              <Select value={f.condition} onValueChange={v => upd("condition", v)}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{["Əla","Yaxşı","Orta","Vurulmuş","Təmir tələb edir"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Təsvir"><Textarea value={f.description} onChange={e => upd("description", e.target.value)} rows={4} className="rounded-xl" /></Field>
          <Button data-testid="step1-next" onClick={() => setStep(2)} className="w-full rounded-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold">Növbəti addım</Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-bold text-xl mb-2">Auksion & Şəkillər</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Başlanğıc qiymət (AZN) *"><Input data-testid="auc-start-price" type="number" value={f.starting_price} onChange={e => upd("starting_price", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Minimum artım (AZN) *"><Input data-testid="auc-increment" type="number" value={f.min_bid_increment} onChange={e => upd("min_bid_increment", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Başlanma vaxtı *"><Input data-testid="auc-start-time" type="datetime-local" value={f.start_time} onChange={e => upd("start_time", e.target.value)} className="rounded-xl h-11" /></Field>
            <Field label="Bitmə vaxtı *"><Input data-testid="auc-end-time" type="datetime-local" value={f.end_time} onChange={e => upd("end_time", e.target.value)} className="rounded-xl h-11" /></Field>
          </div>
          <Field label="Şəkillər (max 10)">
            <label className={`flex flex-col items-center justify-center h-32 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 cursor-pointer bg-slate-50 transition-colors ${uploadingCount ? "opacity-60 pointer-events-none" : ""}`}>
              {uploadingCount ? (
                <>
                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin mb-2" />
                  <span className="text-sm text-slate-600 font-semibold">{uploadingCount} şəkil optimallaşdırılır...</span>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-600 font-semibold">Şəkil yüklə</span>
                  <span className="text-xs text-slate-400 mt-1">Avtomatik sıxılacaq (max 1600px)</span>
                </>
              )}
              <input data-testid="img-upload" type="file" multiple accept="image/*" onChange={uploadImage} className="hidden" />
            </label>
          </Field>
          {images.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {images.map((img, i) => (
                <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100">
                  <img src={img.thumb || img.data} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => { setImages(images.filter(x => x.id !== img.id)); if (main >= images.length - 1) setMain(0); }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center"><X className="h-3 w-3" /></button>
                  <button type="button" onClick={() => setMain(i)} className={`absolute bottom-1 left-1 rounded-full h-6 w-6 flex items-center justify-center ${main === i ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`}><Star className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full h-12">Geri</Button>
            <Button data-testid="create-auction-submit" onClick={submit} className="flex-1 rounded-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold">Auksiona ödəniş et</Button>
          </div>
        </div>
      )}

      {step === 3 && payment && (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="font-extrabold text-2xl mb-2">Ödəniş: {payment.amount} AZN</h2>
          <p className="text-slate-600 mb-6">Auksionun yayımlanması üçün listinq haqqını ödəyin. Payriff təhlükəsiz ödəniş sistemi vasitəsilə.</p>
          <div className="space-y-3">
            <Button data-testid="payriff-pay-btn" onClick={startPayriff} className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-8 font-semibold">
              Payriff ilə ödə
            </Button>
            <button onClick={mockPay} data-testid="mock-pay-btn" className="text-xs text-slate-400 hover:text-slate-600 underline">
              (Test rejimi — ödənişi keç)
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-6">Auksion yalnız ödəniş uğurla tamamlandıqdan sonra yayımlanır.</p>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
